import { ConfigTypes, logger, RemoteConfigService } from '@web_opxp/features';
import filter from 'lodash/filter';
import find from 'lodash/find';
import findIndex from 'lodash/findIndex';
import findLast from 'lodash/findLast';
import sortBy from 'lodash/sortBy';
import { NORMALIZED_MAX_SCORE, WORKOUT_PREFERENCE_STAT } from '../base/constants';
import DomainCalculaionConfig from '../base/domain-calculation-config.json';
import ENV from '../base/env';
import TrainingConfig from '../base/training-config.json';
import { EDomain, IAssessmentConfigOPXP, IDomainCalculation } from '../base/types';
import { MAX_DOMAIN_VALUE } from './constants';
import { AssessmentError } from './types';

export function getAssessmentLength(sessionId: number, config?: IAssessmentConfigOPXP) {
  if (!config) {
    return 0;
  }
  const assessmentSession = config.assessment_ids[sessionId];
  if (!assessmentSession) {
    throw new AssessmentError('Assessment session configuration not found!');
  }
  return assessmentSession.length;
}

export function workoutIsInConfig(workoutId: number, sessionId: number, config: IAssessmentConfigOPXP) {
  const assessmentSession = config.assessment_ids[sessionId];
  if (!assessmentSession) {
    return false;
  }
  const workout = find(assessmentSession, ['id', workoutId]);
  return !!workout;
}

export function findWorkoutInConfig(workoutId: number, sessionId: number, config: IAssessmentConfigOPXP) {
  const assessmentSession = config.assessment_ids[sessionId];
  if (!assessmentSession) {
    throw new AssessmentError('Assessment session configuration not found!');
  }
  const workout = find(assessmentSession, ['id', workoutId]);
  if (!workout) {
    throw new AssessmentError(`Workout ${workoutId} not found in the assessment configuration!`);
  }
  return workout;
}

export function getWorkoutIndexFromConfig(workoutId: number, sessionId: number, config: IAssessmentConfigOPXP) {
  const assessmentSession = config.assessment_ids[sessionId];
  if (!assessmentSession) {
    throw new AssessmentError('Assessment session configuration not found!');
  }
  const index = findIndex(assessmentSession, ['id', workoutId]);
  if (index === -1) {
    throw new AssessmentError(`Workout ${workoutId} not found in the assessment configuration!`);
  }
  return index;
}

export function getLastEvaluation(domains: IDomainCalculation[]) {
  if (domains.length === 0) {
    return domains;
  }
  // get only last cycle
  const sortedByCycle = sortBy(domains, 'cycleId');
  const cycleId = sortedByCycle[sortedByCycle.length - 1].cycleId;
  const filteredByCycle = filter(sortedByCycle, ['cycleId', cycleId]);
  // sort by value
  const sortedByValue = sortBy(filteredByCycle, 'value');
  return sortedByValue;
}

export const removeCFQDomains = (domains: IDomainCalculation[]) => {
  return filter(domains, (domain) => domain.domainId < EDomain.SelfRegulation);
};

export const filterCFQEvaluationDomains = (domains: IDomainCalculation[]) => {
  return filter(domains, (domain) => domain.domainId > EDomain.CFQScore);
};

export const findPredefinedValue = (domainCalculationConfig: typeof DomainCalculaionConfig, domainId: EDomain) => {
  return find(domainCalculationConfig.predefined_values, ['domain_id', domainId]);
};

function calcComplementaryValue(value: number, coefficient: number) {
  return Math.pow((MAX_DOMAIN_VALUE - value) / MAX_DOMAIN_VALUE, coefficient);
}

export function calculateDomainNormalizedComplementoryValues(
  domainScores: IDomainCalculation[],
  domainCalculationConfig: typeof DomainCalculaionConfig
) {
  let sumComplementoryValues = 0;
  for (const score of domainScores) {
    sumComplementoryValues += calcComplementaryValue(
      score.value,
      domainCalculationConfig.complementary_value_coefficient
    );
  }
  // security when sumComplementoryValues == 0 to be able to divide by it
  sumComplementoryValues = Math.max(sumComplementoryValues, 0.0001);
  // normalize values and apply predefined/perosnalization settings
  const normalizedValues: Map<number, number> = new Map();
  for (const score of domainScores) {
    let normalizedValue =
      calcComplementaryValue(score.value, domainCalculationConfig.complementary_value_coefficient) /
      sumComplementoryValues;
    logger(
      `normalized value for domain ${EDomain[score.domainId]}: ${normalizedValue} = ${
        score.value
      } / ${sumComplementoryValues}`
    );
    const predefinedDomainValue = findPredefinedValue(domainCalculationConfig, score.domainId);
    if (predefinedDomainValue) {
      normalizedValue =
        domainCalculationConfig.personalisation_percentage * normalizedValue +
        (1 - domainCalculationConfig.personalisation_percentage) * predefinedDomainValue.value;
    }
    normalizedValues.set(score.domainId, normalizedValue);
  }
  logger(['Normalized complementary values:', normalizedValues]);
  return normalizedValues;
}

export const calculateCFQDomainScores = (
  cycleId: number,
  resultByQuestion: Map<number, number>,
  cognitiveFailureWeights: Map<EDomain, ConfigTypes.IWorkoutDomainWeight[]>
) => {
  const domainScores: IDomainCalculation[] = [];
  for (const [domainId, failure] of cognitiveFailureWeights.entries()) {
    let sumOfCognitiveFalures = 0;
    let cognitiveFaluresScore = 0;
    for (const cognitiveFaluresWeight of failure) {
      if (resultByQuestion.has(cognitiveFaluresWeight.workoutId)) {
        sumOfCognitiveFalures += 4;
        cognitiveFaluresScore += resultByQuestion.get(cognitiveFaluresWeight.workoutId) ?? 0;
      }
    }
    if (sumOfCognitiveFalures > 0) {
      const domainScore = (NORMALIZED_MAX_SCORE * cognitiveFaluresScore) / sumOfCognitiveFalures;
      domainScores.push({
        cycleId,
        domainId,
        value: Math.round(domainScore),
        timestamp: -1,
      });
    }
  }
  return domainScores;
};

export function cfqDomainIsPresent(domains: IDomainCalculation[]) {
  for (const domain of domains) {
    if (domain.domainId > EDomain.CFQScore) {
      return true;
    }
  }
  return false;
}

export const getAssessmentConfigForCycle = (cycleId: number) => {
  const trainingConfig = RemoteConfigService.getObjectConfigValue<typeof TrainingConfig>(
    ENV.RemoteConfigTraining,
    TrainingConfig
  );
  if (!trainingConfig) {
    throw new Error('Assessment configuration is unavailable!');
  }
  const assessments = trainingConfig.assessment_config as Array<IAssessmentConfigOPXP>;
  const cycleAssessmentConfig = findLast(assessments, (config) => {
    return config.from_cycle_id <= cycleId;
  });
  if (!cycleAssessmentConfig) {
    throw new Error('Assessment configuration is unavailable!');
  }
  return cycleAssessmentConfig;
};

export const getGtmTime = (hour: number): number => {
  const date = new Date();
  let result = hour + date.getTimezoneOffset() / 60;
  // adjust result if it is negative or bigger than 23
  if (result < 0) result += 24;
  if (result > 23) result -= 24;
  return result;
};

export function parseWorkoutPreferenceStatId(workoutId: number) {
  return WORKOUT_PREFERENCE_STAT.replace('x', `${workoutId}`);
}
